import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  NavBarList,
  spacer32,
  spacer4,
  TableContainer,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { useProperty } from '@spotify-internal/remote-config-integration-react';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useHasMusicVideoAccess } from '@hooks/useHasMusicVideoAccess';
import { useProfileProtections } from '@hooks/useProfileProtections';
import { StoredSearchResult } from '@hooks/useSearchedAlbums/types';
import { useTabsWithQueryParams } from '@hooks/useTabsWithQueryParams';
import { HeldReleases } from '@components/Catalog/ContentArea/HeldReleases';
import { HeldInReviewNavBar } from '@components/Catalog/ContentArea/HeldReleases/HeldInReviewNavBar';
import { NavBarListItemAsLink } from '@components/shared/NavBarListItemAsLink';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { unclearedMusicVideosRollout } from '@lib/remoteConfig/properties';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { focusActiveTab } from '@helpers/focusActiveTab';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { CatalogExports } from './CatalogExports';
import {
  DEFAULT_DATE_FILTER_OPTION,
  HeldReleasesFilter,
} from './HeldReleases/HeldReleasesFilter';
import { ProfileProtections } from './ProfileProtections';
import { RecentlyViewed } from './RecentlyViewed';
import { UnassociatedVideos } from './UnassociatedVideos';
import { UnassociatedVideosNavBar } from './UnassociatedVideos/UnassociatedVideosNavbar';
import { UnclearedVideos } from './UnclearedVideos';
import { UnclearedVideosNavbar } from './UnclearedVideos/UnclearedVideosNavbar';

const TableContainerWithTopMargin = styled(TableContainer)`
  margin-top: calc(${spacer32} + ${spacer4});
`;

export const gaEvents = {
  handleTableRowClick: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Tablerow clicked',
      product: GA_PRODUCT_CATALOG,
    }),
  handleClickCopyReleaseId: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Release ID copied',
      product: GA_PRODUCT_CATALOG,
    }),
  handleClickCopyURI: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'URI copied',
      product: GA_PRODUCT_CATALOG,
    }),
  handleExpandTerritoriesClick: (show: boolean) =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.TOGGLE_ROW,
      eventLabel: `Expanded: ${show}`,
      product: GA_PRODUCT_CATALOG,
    }),
  handleLiveInTerritoriesApiRetry: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.TOGGLE_ROW,
      eventLabel: 'Retried live in territories info fetch',
      product: GA_PRODUCT_CATALOG,
    }),
  // Tabs which determine the content area
  handleRecentlyViewedTabClick: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Recently Viewed Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handleHeldReleasesTabClickGAEvent: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Held Releases Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handleUnassociatedVideosTabClickGAEvent: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Unassociated Videos Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handleCatalogExportsTabClick: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Catalog Exports Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handleUnclearedVideosTabClickGAEvent: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Uncleared Videos Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handleProfileProtectionsTabClickGAEvent: () =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_CONTENT_TABS,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Profile Protections Tab Viewed',
      product: GA_PRODUCT_CATALOG,
    }),
  handlePaginationClick: (actionType: 'increment' | 'decrement') =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Pagination clicked: ${actionType}`,
      product: GA_PRODUCT_CATALOG,
    }),
  handleLimitChange: (updatedLimitValue: number) =>
    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_PAGE_RECENTLY_VIEWED,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Pagination limit changed: ${updatedLimitValue}`,
      product: GA_PRODUCT_CATALOG,
    }),
};

const StyledType = styled(Type)`
  display: flex;
  align-items: center;
`;

const ContentAreaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RECENTLY_VIEWED = 'recentlyViewed';
const HELD_IN_REVIEW = 'heldInReview';
const CATALOG_EXPORTS = 'catalogExports';
const CATALOG_EXPORTS_V2 = 'catalogExportsv2';
const UNASSOCIATED_VIDEOS = 'unassociatedVideos';
const UNCLEARED_VIDEOS = 'unclearedVideos';
const PROFILE_PROTECTIONS = 'profileProtections';

type PageTabs =
  | typeof RECENTLY_VIEWED
  | typeof HELD_IN_REVIEW
  | typeof CATALOG_EXPORTS
  | typeof CATALOG_EXPORTS_V2
  | typeof UNASSOCIATED_VIDEOS
  | typeof UNCLEARED_VIDEOS
  | typeof PROFILE_PROTECTIONS;

const ALL_PAGE_TABS: PageTabs[] = [
  RECENTLY_VIEWED,
  HELD_IN_REVIEW,
  CATALOG_EXPORTS,
  CATALOG_EXPORTS_V2,
  UNASSOCIATED_VIDEOS,
  UNCLEARED_VIDEOS,
  PROFILE_PROTECTIONS,
];

interface ContentAreaProps {
  releasesToShow: StoredSearchResult[];
  userFeeds: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[];
  userLicensors: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[];
  paginationProps: {
    page: string;
    limit: string;
    handlePagination: (updatedPage: number) => void;
    handleLimitChange: (updatedLimit: number) => void;
  };
}

export const ContentArea = ({
  releasesToShow,
  userFeeds,
  userLicensors,
  paginationProps,
}: ContentAreaProps) => {
  const t = useT();

  const { hasAccess: hasMusicVideoAccess } = useHasMusicVideoAccess();
  const unclearedVideosFlag = useProperty(unclearedMusicVideosRollout);
  const { isProfileProtectionsEnabled } = useProfileProtections();

  const {
    activePageTab,
    actions: { buildTabParam },
  } = useTabsWithQueryParams<PageTabs>(RECENTLY_VIEWED, ALL_PAGE_TABS);
  const [onHeldDateFilter, setOnHeldDateFilter] = useState(
    DEFAULT_DATE_FILTER_OPTION,
  );

  const RECENTLY_VIEWED_PARAM = buildTabParam(RECENTLY_VIEWED);
  const CATALOG_EXPORTS_PARAM = buildTabParam(CATALOG_EXPORTS);
  const HELD_IN_REVIEW_PARAM = buildTabParam(HELD_IN_REVIEW);
  const UNASSOCIATED_VIDEOS_PARAM = buildTabParam(UNASSOCIATED_VIDEOS);
  const UNCLEARED_VIDEOS_PARAM = buildTabParam(UNCLEARED_VIDEOS);
  const PROFILE_PROTECTIONS_PARAM = buildTabParam(PROFILE_PROTECTIONS);

  const onBackArrow = () => {
    focusActiveTab();
  };
  const handlePopStateChange = useCallback(() => {
    setTimeout(() => onBackArrow(), 0);
  }, []);
  useEffect(() => {
    window.addEventListener('popstate', handlePopStateChange);
    return () => {
      window.removeEventListener('popstate', handlePopStateChange);
    };
  }, [handlePopStateChange]);

  return (
    <>
      <ContentAreaHeader>
        <NavBarList>
          <NavBarListItemAsLink
            active={activePageTab === RECENTLY_VIEWED}
            href={RECENTLY_VIEWED_PARAM}
            onClick={() => gaEvents.handleRecentlyViewedTabClick}
            sentenceCase
          >
            <Type variant="celloCanon">
              {t(
                'atlas.recently_viewed',
                'Recently viewed',
                'Title for Recently viewed section',
              )}
            </Type>
          </NavBarListItemAsLink>

          <HeldInReviewNavBar
            isActive={activePageTab === HELD_IN_REVIEW}
            hrefParam={HELD_IN_REVIEW_PARAM}
            handleHeldReleasesTabClickGAEvent={() =>
              gaEvents.handleHeldReleasesTabClickGAEvent()
            }
            userLicensors={userLicensors}
          />

          <NavBarListItemAsLink
            active={activePageTab === CATALOG_EXPORTS}
            href={CATALOG_EXPORTS_PARAM}
            onClick={() => gaEvents.handleCatalogExportsTabClick()}
            sentenceCase
          >
            <StyledType variant="celloCanon">
              {t(
                'atlas.search.catalog_exports',
                'Catalog exports',
                'Title for Catalog exports section',
              )}
            </StyledType>
          </NavBarListItemAsLink>

          {hasMusicVideoAccess && unclearedVideosFlag && (
            <UnclearedVideosNavbar
              isActive={activePageTab === UNCLEARED_VIDEOS}
              hrefParam={UNCLEARED_VIDEOS_PARAM}
              handleUnclearedVideosTabClickGAEvent={() =>
                gaEvents.handleUnclearedVideosTabClickGAEvent()
              }
              userLicensors={userLicensors}
            />
          )}

          {hasMusicVideoAccess && (
            <UnassociatedVideosNavBar
              isActive={activePageTab === UNASSOCIATED_VIDEOS}
              hrefParam={UNASSOCIATED_VIDEOS_PARAM}
              handleUnassociatedVideosTabClickGAEvent={() =>
                gaEvents.handleUnassociatedVideosTabClickGAEvent()
              }
            />
          )}

          {isProfileProtectionsEnabled && (
            <NavBarListItemAsLink
              active={activePageTab === PROFILE_PROTECTIONS}
              href={PROFILE_PROTECTIONS_PARAM}
              onClick={() => gaEvents.handleProfileProtectionsTabClickGAEvent()}
              sentenceCase
            >
              <Type variant="celloCanon">
                {t(
                  'atlas.profile_protections',
                  'Profile protections',
                  'Title for Profile protections tab',
                )}
              </Type>
            </NavBarListItemAsLink>
          )}
        </NavBarList>
        {activePageTab === HELD_IN_REVIEW && (
          <HeldReleasesFilter onChangeDateFilter={setOnHeldDateFilter} />
        )}
      </ContentAreaHeader>

      <TableContainerWithTopMargin>
        {activePageTab === RECENTLY_VIEWED && (
          <RecentlyViewed
            releasesToShow={releasesToShow}
            gaEvents={gaEvents}
            userFeeds={userFeeds}
            userLicensors={userLicensors}
            paginationProps={{
              handlePagination: paginationProps.handlePagination,
              handleLimitChange: paginationProps.handleLimitChange,
              limit: paginationProps.limit,
              page: paginationProps.page,
            }}
          />
        )}
        {activePageTab === CATALOG_EXPORTS && <CatalogExports />}
        {activePageTab === HELD_IN_REVIEW && (
          <HeldReleases
            userLicensors={userLicensors}
            filter={{ onHeldDateFilter }}
          />
        )}
        {activePageTab === UNCLEARED_VIDEOS && (
          <UnclearedVideos userLicensors={userLicensors} />
        )}
        {activePageTab === UNASSOCIATED_VIDEOS && <UnassociatedVideos />}
        {activePageTab === PROFILE_PROTECTIONS && <ProfileProtections />}
      </TableContainerWithTopMargin>
    </>
  );
};
